<template>
  <sidebar
    :addTitle="$t('GradeControl.Forecast.Annual.AddNew')"
    :editTitle="$t('GradeControl.Forecast.Annual.Edit')"
    :isSidebarActive="isSidebarActive"
    :data="data"
    @onNewData="newData"
    @onEditData="editData"
    @closeSidebar="closeSidebar">

    <ValidationObserver v-slot="{ handleSubmit }">
      <form-wizard
        ref="wizard"
        color="rgba(var(--vs-primary), 1)"
        errorColor="rgba(var(--vs-danger), 1)"
        :title="null"
        :subtitle="null"
        :nextButtonText="$t('Next')"
        :backButtonText="$t('Back')"
        :finishButtonText="$t('Submit')"
        @on-complete="handleSubmit(submitData)"
      >
        <tab-content :title="$t('GradeControl.Forecast.Annual.Step1')" class="mb-2" :before-change="validateStep1">
          <ValidationObserver ref="step1Observer">
            <form data-vv-scope="step-1">
              <vs-row class="mb-3">
                <vs-col vs-w="4">
                  <form-label>{{ $t('GradeControl.Forecast.Annual.Year') }}</form-label>
                  <strong class="ml-2">{{ year }}</strong>
                </vs-col>
                <vs-col vs-w="4">
                  <form-label>{{ $t('StartDate') }}</form-label>
                  <strong class="ml-2">{{ period.startDate }}</strong>
                </vs-col>
                <vs-col vs-w="4">
                  <form-label>{{ $t('EndDate') }}</form-label>
                  <strong class="ml-2">{{ period.endDate }}</strong>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="7" class="mr-5">
                  <form-label>{{ $t('GradeControl.Location.Type') }}</form-label>
                  <ValidationProvider :name="$t('GradeControl.Location.Type')" rules="required" v-slot="{ errors }">
                    <v-select
                      label="name"
                      :options="locationTypes"
                      v-model="forecastLocationType"
                      :reduce="locationType => locationType.id"
                      class="w-full"
                    />
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col class="mt-10" vs-w="4">
                  <vs-checkbox v-model="onlyActiveLocation" class="mb-2">{{ $t('GradeControl.Location.OnlyActive') }}</vs-checkbox>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <form-label>{{ $t('GradeControl.Location.Name') }}</form-label>
                  <ValidationProvider :name="$t('GradeControl.Location.Name')" rules="required" v-slot="{ errors }">
                    <v-select
                      label="code"
                      :options="filteredLocations"
                      :reduce="location => location.locationId"
                      v-model="forecastLocationId"
                      class="w-full"
                    />
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <form-label>{{ $t('GradeControl.Forecast.Annual.Category') }}</form-label>
                  <v-select
                    label="name"
                    :options="resourceTypes"
                    :reduce="resourceType => resourceType.id"
                    v-model="forecastResourceType"
                    class="w-full"
                  />
                </vs-col>
              </vs-row>
              <vs-row class="mb-5" vs-type="flex" vs-justify="space-between">
                <vs-col vs-w="6" class="mr-10">
                  <ValidationProvider :name="$t('GradeControl.Forecast.Tonnage')" rules="required|decimal|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$systemUnits.getTonnageLabel()"
                      v-model="forecastTonnage"
                    ></validated-input>
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="5">
                  <ValidationProvider :name="$t('GradeControl.Density')" rules="required|decimal" v-slot="{ errors }">
                    <validated-input
                      :label="$systemUnits.getDensityLabel()"
                      v-model="forecastDensity"
                    ></validated-input>
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
            </form>
          </ValidationObserver>
        </tab-content>
        <tab-content :title="$t('GradeControl.Forecast.Annual.Step2')" class="mb-2" :before-change="validateStep2">
          <ValidationObserver ref="step2Observer">
            <form data-vv-scope="step-2">
              <vs-row v-for="entry in entries" :key="entry.element" class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left" class="mb-2">{{ elementName(entry.element) }}</vs-divider>
                <vs-col vs-w="6" class="mr-5">
                  <ValidationProvider :name="elementName(entry.element)" rules="decimal|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Forecast.Annual.Grade')"
                      v-model="entry.grade"
                    ></validated-input>
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="5">
                  <form-label>{{ $t('GradeControl.Forecast.Annual.Unit') }}</form-label>
                  <v-select
                    label="name"
                    :options="unitOptions"
                    :reduce="unit => unit.id"
                    :clearable="false"
                    v-model="entry.gradeUnit"
                    class="w-full"
                  />
                </vs-col>
                <!-- An imported row keeps its metal quantity; typing a grade above replaces it. -->
                <vs-col vs-w="12" v-if="entry.quantity !== null">
                  <small class="imported-quantity">
                    {{ $t('GradeControl.Forecast.Annual.ImportedQuantity') }} : {{ formatNumber(entry.quantity) }} {{ unitName(entry.quantityUnit) }}
                    <span v-if="importedGrade(entry)"> = {{ formatNumber(importedGrade(entry).value) }} {{ unitName(importedGrade(entry).unit) }}</span>
                  </small>
                </vs-col>
              </vs-row>
            </form>
          </ValidationObserver>
        </tab-content>
      </form-wizard>
    </ValidationObserver>
  </sidebar>
</template>

<script>
import { FormWizard, TabContent } from "vue-form-wizard";
import vSelect from 'vue-select';
import {
  FormLabel,
  ValidatedInput,
  ValidationMessage,
  Sidebar
} from "@/app/shared/components";
import ElementEnum from "@/app/shared/ElementEnum.js";
import SampleUnitEnum from "@/app/shared/SampleUnitEnum.js";
import MRMRResourceTypeEnum from "@/app/shared/MRMRResourceTypeEnum.js";
import LocationTypeEnum from "../../LocationTypeEnum.js";
import { buildEntries, buildResults, gradeOf, yearPeriod, UNIT } from "./annualForecastRules.js";

const FORM_UNITS = [UNIT.GramPerTon, UNIT.Percent, UNIT.ppm];

export default {
  name: "AnnualForecastSideView",
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true
    },
    data: {
      type: Object,
      default: () => {}
    },
    locations: {
      type: Array,
      required: true
    },
    // Element ids of the project; falls back to Au, Ag, Cu, Zn, Pb when empty.
    elements: {
      type: Array,
      default: () => []
    },
    year: {
      type: Number,
      required: true
    },
    projectId: {
      type: Number,
      required: true
    }
  },
  components: {
    FormWizard,
    TabContent,
    Sidebar,
    'v-select': vSelect,
    FormLabel,
    ValidatedInput,
    ValidationMessage
  },
  data() {
    return {
      forecastId: 0,
      forecastLocationType: null,
      forecastLocationId: null,
      forecastResourceType: null,
      forecastTonnage: null,
      forecastDensity: null,
      onlyActiveLocation: true,
      entries: [],
      locationTypes: LocationTypeEnum.definition,
      resourceTypes: MRMRResourceTypeEnum.definition,
      unitOptions: SampleUnitEnum.definition.filter(unit => FORM_UNITS.includes(unit.id))
    };
  },
  computed: {
    period() {
      return yearPeriod(this.year);
    },
    filteredLocations() {
      return this.locations.filter(l => l.type === this.forecastLocationType && (!this.onlyActiveLocation || l.isActive === true));
    }
  },
  watch: {
    forecastLocationId(val) {
      if (!val) return;
      if (this.forecastDensity === null || this.forecastDensity === '' || this.forecastDensity === 0) {
        const location = this.locations.find(l => l.locationId === val);
        if (location) {
          this.forecastDensity = this.$systemUnits.convertDensity(location.density);
        }
      }
    }
  },
  methods: {
    elementName(element) {
      return ElementEnum.getNameFor(element);
    },
    unitName(unit) {
      return unit === null || unit === undefined ? '' : SampleUnitEnum.getNameFor(unit);
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '';
      const number = Number(value);
      return Number.isInteger(number) ? number.toString() : number.toFixed(2);
    },
    importedGrade(entry) {
      return gradeOf(entry, this.$systemUnits.convertTonnageToMetric(this.forecastTonnage));
    },
    newData() {
      this.forecastId = 0;
      this.forecastLocationType = null;
      this.forecastLocationId = null;
      this.forecastResourceType = null;
      this.forecastTonnage = null;
      this.forecastDensity = null;
      this.onlyActiveLocation = true;
      this.entries = buildEntries(this.elements, []);
      this.$refs.wizard.reset();
    },
    editData() {
      const { annualLocationForecastId, locationId, resourceType, density, tonnage, results } = JSON.parse(JSON.stringify(this.data));
      this.forecastId = annualLocationForecastId;
      this.forecastLocationId = locationId;
      const location = this.locations.find(l => l.locationId === locationId);
      this.forecastLocationType = location ? location.type : null;
      this.onlyActiveLocation = location ? location.isActive : true;
      this.forecastResourceType = resourceType === undefined ? null : resourceType;
      this.forecastDensity = this.$systemUnits.convertDensity(density);
      this.forecastTonnage = this.$systemUnits.convertTonnage(tonnage);
      this.entries = buildEntries(this.elements, results);
      this.$refs.wizard.reset();
    },
    closeSidebar() {
      this.$emit("closeSidebar");
    },
    validateStep1() {
      return this.validateStep(this.$refs.step1Observer);
    },
    validateStep2() {
      return this.validateStep(this.$refs.step2Observer);
    },
    validateStep(observer) {
      return new Promise((resolve, reject) => {
        observer.validate().then(result => {
          if (result) {
            resolve(true);
          } else {
            reject("correct all values");
          }
        });
      });
    },
    submitData() {
      // Grades carry their own unit and imported quantities are kept as stored, so only tonnage and density go
      // through the project unit system.
      const annualLocationForecast = {
        annualLocationForecastId: this.forecastId,
        locationId: this.forecastLocationId,
        resourceType: this.forecastResourceType,
        startDate: this.period.startDate,
        endDate: this.period.endDate,
        tonnage: this.$systemUnits.convertTonnageToMetric(this.forecastTonnage),
        density: this.$systemUnits.convertDensityToMetric(this.forecastDensity),
        results: buildResults(this.entries)
      };
      const payload = { projectId: this.projectId, annualLocationForecast };
      const isEdit = this.forecastId !== null && this.forecastId > 0;

      this.$vs.loading();
      this.$store.dispatch(isEdit ? "gradeControl/updateAnnualForecast" : "gradeControl/addAnnualForecast", payload)
        .then((response) => {
          this.$emit(isEdit ? "onForecastUpdated" : "onForecastInserted", response.data.model);
        })
        .catch(() => {
          // The store already notified the error; the form stays open with its values.
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.imported-quantity {
  display: block;
  margin: -0.5rem 0 0.75rem 0;
  color: rgba(0, 0, 0, 0.6);
}
</style>
